import type {ILatLonCoordinate} from "@reva/shared/geo-reference";
import type {ValuationData} from "@reva/shared/valuation-data";
import type {RevaValuationPurpose} from "@reva/shared/valuation-purpose";

import type ServiceApi from "../../api";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";

/** Returns the object if it has at least one key, otherwise null — so empty parameter groups are sent as null instead of {}. */
function trimObjectToNull(obj: object): object | null {
    return Object.keys(obj).length > 0 ? obj : null;
}

/**
 * API interface for creating a new valuation in the persistence (POST {@link RevaApi.PATH_VALUATIONS}).
 */
export class PersistenceInitializationApiInterface extends SimpleApiInterface<ValuationData> {
    private readonly addressQuery: string;
    private readonly coordinate: ILatLonCoordinate;
    private readonly segment: string;
    private readonly parameters: object;
    private readonly documentProperties: object;
    private readonly registerParameters: object;
    private readonly energyCertificateParameters: object;
    private readonly plotParameters: object;
    private readonly inspectionDerivedParameters: object;
    private readonly immodropParameters: object;
    private readonly purpose: RevaValuationPurpose | null;

    /**
     * @param purpose Zweck der neuen REVA-ID (#19481); null = Feld weglassen, dann wählt das Backend den Zweck
     *   aus den Rechten des Users (Bewertung vor Research)
     */
    constructor(
        addressQuery: string,
        coordinate: ILatLonCoordinate,
        segment: string,
        parameters: object,
        documentProperties: object,
        registerParameters: object,
        energyCertificateParameters: object,
        plotParameters: object,
        inspectionDerivedParameters: object,
        immodropParameters: object,
        purpose: RevaValuationPurpose | null = null,
    ) {
        super();
        this.addressQuery = addressQuery;
        // Reduce the (possibly richer) coordinate source down to the bare {lat, lon} the API expects.
        this.coordinate = {lat: coordinate.lat, lon: coordinate.lon};
        this.segment = segment;
        this.parameters = parameters;
        this.documentProperties = documentProperties;
        this.registerParameters = registerParameters;
        this.energyCertificateParameters = energyCertificateParameters;
        this.plotParameters = plotParameters;
        this.inspectionDerivedParameters = inspectionDerivedParameters;
        this.immodropParameters = immodropParameters;
        this.purpose = purpose;
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([RevaApi.PATH_VALUATIONS]);
    }

    protected buildPayload(): object {
        return {
            addressQuery: this.addressQuery,
            coordinate: this.coordinate,
            segment: this.segment,
            parameters: this.parameters,
            documentProperties: trimObjectToNull(this.documentProperties),
            registerParameters: trimObjectToNull(this.registerParameters),
            energyCertificateParameters: trimObjectToNull(this.energyCertificateParameters),
            plotParameters: trimObjectToNull(this.plotParameters),
            inspectionDerivedParameters: trimObjectToNull(this.inspectionDerivedParameters),
            immodropParameters: trimObjectToNull(this.immodropParameters),
            ...(this.purpose ? {purpose: this.purpose} : {}),
        };
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<ValuationData> {
        return executor.fetchPOST(url, credentials, this.buildPayload());
    }
}
